import {createAsyncThunk, createEntityAdapter, createSlice} from '@reduxjs/toolkit';
import {startLoading, stopLoading} from 'app/store/utilSlice';
import {apiGet, apiPut} from 'app/shared-components/util/restAPI';

const publicArticleAdapter = createEntityAdapter({
	sortComparer: (a, b) => (b.publishDate || b.createdAt) - (a.publishDate || a.createdAt)
});

const featuredArticleAdapter = createEntityAdapter({
	sortComparer: (a, b) => (b.publishDate || b.createdAt) - (a.publishDate || a.createdAt)
});

export const fetchPublicArticle = createAsyncThunk(
  "marketing/publicArticles/fetchPublicArticle",
  async (slugOrId, { dispatch }) => {
	dispatch(startLoading());
	try{
      const apiName = 'PublicAPI3';
	  const path = '/x3/article/public/byslug?slug=' + encodeURIComponent(slugOrId);
      const options = {
		  headers: {},
		  response: false,
	  };

	  const data = await apiGet(apiName, path, options);
	  return data;
	}finally{
	  dispatch(stopLoading());
	}
  }
);

export const fetchFeaturedArticles = createAsyncThunk(
  "marketing/publicArticles/fetchFeaturedArticles",
  async (_, { dispatch }) => {
	dispatch(startLoading());
	try{
      const apiName = 'PublicAPI';
	  const path = '/search/public/articles';
      const options = {
		  headers: {},
		  body: {
			  featured: true,
			  limit: 9,
			  offset: 0
		  },
		  response: false,
	  };

	  const data = await apiPut(apiName, path, options);
	  return data;
	}finally{
	  dispatch(stopLoading());
	}
  }
);

export const searchPublicArticles = createAsyncThunk(
  "marketing/publicArticles/searchPublicArticles",
  async (params, { dispatch }) => {
	dispatch(startLoading());
	try{
      const apiName = 'PublicAPI';
	  const path = '/search/public/articles';
      const options = {
		  headers: {},
		  body: {
			  keywords: params.keywords || params.s || '',
			  limit: params.limit || 9,
			  offset: params.offset || 0,
			  s: undefined,
			  ...params

		  },
		  response: false,
	  };

	  const data = await apiPut(apiName, path, options);
	  return { data: data.data || data, loadMore: params.loadMore };
	}finally{
	  dispatch(stopLoading());
	}
  }
);

export const fetchPublicArticlesByOwner = createAsyncThunk(
  "marketing/publicArticles/fetchPublicArticlesByOwner",
  async (ownerKey, { dispatch }) => {
	dispatch(startLoading());
	try{
      const apiName = 'PublicAPI3';
	  const path = '/x3/article/public/fetchbyowner?owner=' + ownerKey;
      const options = {
		  headers: {},
		  response: false,
	  };

	  const data = await apiGet(apiName, path, options);
	  return data;
	}finally{
	  dispatch(stopLoading());
	}
  }
);

export const updatePublicArticle = createAsyncThunk(
  "marketing/publicArticles/updatePublicArticle",
  async (updateData, { dispatch }) => {
	dispatch(startLoading());
	try{
      const apiName = 'CoreAPI3';
	  const path = '/x3/article/' + updateData.id;
      const options = {
		  headers: {},
		  body: updateData,
		  response: false,
	  };

	  const data = await apiPut(apiName, path, options);
	  return data;
	}finally{
	  dispatch(stopLoading());
	}
  }
);

const publicArticlesSlice = createSlice({
  name: "marketing/publicArticles",
  initialState: {
	  articles: publicArticleAdapter.getInitialState(),
	  featuredArticles: featuredArticleAdapter.getInitialState(),
	  searchedArticles: [],
	  articlePagination: { total: 0 },
	  articlesByOwner: {},
	  slugToIdMap: {},
	  featuredOnly: true,
	  keywords: '',
	  page: 1,
	  lastSearchKey: '',
  },
  reducers: {
	  setFeaturedOnly: (state, action) => {
		  state.featuredOnly = action.payload;
		  state.page = 1;
	  },
	  setKeywords: (state, action) => {
		  state.keywords = action.payload;
		  state.page = 1;
	  },
	  setPage: (state, action) => {
		  state.page = action.payload;
	  },
	  setLastSearchKey: (state, action) => {
		  state.lastSearchKey = action.payload;
	  },
  },
  extraReducers: builder => {
    builder
    .addCase(fetchPublicArticle.fulfilled, (state, action) => {
		const article = action.payload;
		
		publicArticleAdapter.upsertOne(state.articles, article);
		
		// Always map slug to ID if slug exists
		if (article.slug) {
			state.slugToIdMap[article.slug] = article.id;
		}
    })
    .addCase(fetchFeaturedArticles.fulfilled, (state, action) => {
		featuredArticleAdapter.setAll(state.featuredArticles, action.payload);
		
		// Index slugs for featured articles
		action.payload.forEach(article => {
			if (article.slug) {
				state.slugToIdMap[article.slug] = article.id;
			}
		});
    })
    .addCase(searchPublicArticles.fulfilled, (state, action) => {
		if (action.payload.loadMore) {
			state.searchedArticles = [...state.searchedArticles, ...action.payload.data];
		} else {
			state.searchedArticles = action.payload.data;
		}
    })
    .addCase(fetchPublicArticlesByOwner.fulfilled, (state, action) => {
		const ownerKey = action.meta.arg;
		state.articlesByOwner[ownerKey] = action.payload;
    })
    .addCase(updatePublicArticle.fulfilled, (state, action) => {
		const updatedArticle = action.payload;
		// Update in main articles collection
		publicArticleAdapter.upsertOne(state.articles, updatedArticle);
		// Update in featured articles collection
		featuredArticleAdapter.upsertOne(state.featuredArticles, updatedArticle);
		// Update slug mapping
		if (updatedArticle.slug) {
			state.slugToIdMap[updatedArticle.slug] = updatedArticle.id;
		}
		// Update in searched articles array
		const searchIndex = state.searchedArticles.findIndex(article => article.id === updatedArticle.id);
		if (searchIndex !== -1) {
			state.searchedArticles[searchIndex] = updatedArticle;
		}
		// Update in articlesByOwner collections
		Object.keys(state.articlesByOwner).forEach(ownerKey => {
			const ownerArticles = state.articlesByOwner[ownerKey];
			const ownerIndex = ownerArticles.findIndex(article => article.id === updatedArticle.id);
			if (ownerIndex !== -1) {
				state.articlesByOwner[ownerKey][ownerIndex] = updatedArticle;
			}
		});
    })
  }
})

const { selectById: selectPublicArticleByIdFromAdapter, selectAll: selectAllPublicArticlesFromAdapter } = publicArticleAdapter.getSelectors();
const { selectAll: selectAllFeaturedArticlesFromAdapter } = featuredArticleAdapter.getSelectors();

export const selectPublicArticleById = (articleId) => state => {
	return selectPublicArticleByIdFromAdapter(state.marketing.publicArticles.articles, articleId);
}

export const selectPublicArticleBySlugOrId = (slugOrId) => state => {
	const articles = state.marketing.publicArticles.articles;
	const slugToIdMap = state.marketing.publicArticles.slugToIdMap;
	
	// Try direct ID lookup first
	let article = selectPublicArticleByIdFromAdapter(articles, slugOrId);
	
	// If not found and we have a slug mapping, try that
	if (!article && slugToIdMap[slugOrId]) {
		article = selectPublicArticleByIdFromAdapter(articles, slugToIdMap[slugOrId]);
	}
	
	return article;
}

export const selectAllPublicArticles = () => state => {
	return selectAllPublicArticlesFromAdapter(state.marketing.publicArticles.articles);
}

export const selectAllFeaturedArticles = () => state => {
	return selectAllFeaturedArticlesFromAdapter(state.marketing.publicArticles.featuredArticles);
}

export const selectSearchedPublicArticles = () => state => {
	return state.marketing.publicArticles.searchedArticles;
}

export const selectPublicArticlePagination = () => state => {
	return state.marketing.publicArticles.articlePagination;
}

export const selectPublicArticlesByOwner = (ownerKey) => state => {
	return state.marketing.publicArticles.articlesByOwner[ownerKey] || [];
}

export const selectFeaturedOnly = () => state => {
	return state.marketing.publicArticles.featuredOnly;
}

export const selectKeywords = () => state => {
	return state.marketing.publicArticles.keywords;
}

export const selectPage = () => state => {
	return state.marketing.publicArticles.page;
}

export const selectLastSearchKey = () => state => {
	return state.marketing.publicArticles.lastSearchKey;
}

export const { setFeaturedOnly, setKeywords, setPage, setLastSearchKey } = publicArticlesSlice.actions;

export default publicArticlesSlice.reducer;